import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiMail, FiLock } from "react-icons/fi";
import { useAuth } from '../AuthContext';
import './Login.css';
import { USER_ENDPOINTS } from "../../Constants";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const buttonRef = useRef(null);
    const navigate = useNavigate();
    const { login, isAuthenticated } = useAuth();

    // Focus states for inputs
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);

    // Redirect to dashboard if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard');
        }
    }, [isAuthenticated, navigate]);

    // Handle ripple effect on button click
    const createRipple = (event) => {
        const button = event.currentTarget;
        const circle = document.createElement("span");
        const diameter = Math.max(button.clientWidth, button.clientHeight);
        const radius = diameter / 2;

        // Position the ripple exactly where clicked
        const rect = button.getBoundingClientRect();
        circle.style.width = circle.style.height = `${diameter}px`;
        circle.style.left = `${event.clientX - rect.left - radius}px`;
        circle.style.top = `${event.clientY - rect.top - radius}px`;
        circle.classList.add("ripple");

        // Remove existing ripples
        const ripple = button.getElementsByClassName("ripple")[0];
        if (ripple) {
            ripple.remove();
        }

        button.appendChild(circle);
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            setErrorMessage("Vă rugăm să introduceți atât email-ul cât și parola");
            return;
        }

        try {
            setIsLoading(true);
            setErrorMessage("");

            // Simulate a slightly longer request for demo purposes
            await new Promise(resolve => setTimeout(resolve, 1000));

            const response = await axios.post(USER_ENDPOINTS.LOGIN, {
                email,
                password,
            });

            // Use the login function from AuthContext
            login(response.data.token, {
                email: response.data.email,
                fullName: response.data.fullName,
                role: response.data.role
            });

            // If remember me is checked, store the email
            if (rememberMe) {
                localStorage.setItem("rememberedEmail", email);
            } else {
                localStorage.removeItem("rememberedEmail");
            }

            // Redirect to the dashboard page
            navigate("/dashboard");
        } catch (error) {
            console.error("Login error:", error);
            setErrorMessage(
                error.response?.data?.message ||
                "Email sau parolă invalidă. Vă rugăm să încercați din nou."
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Check for remembered email on component mount
    useEffect(() => {
        const savedEmail = localStorage.getItem("rememberedEmail");
        if (savedEmail) {
            setEmail(savedEmail);
            setRememberMe(true);
        }
    }, []);

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-header">
                    <h2>Leu Mob Admin</h2>
                    <p>Vă rugăm să vă autentificați pentru a continua</p>
                </div>

                <div className="login-form-container">
                    <form className="login-form" onSubmit={handleLogin}>
                        <div className={`input-container ${emailFocused ? 'focus' : ''}`}>
                            <label htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Introduceți email-ul"
                                onFocus={() => setEmailFocused(true)}
                                onBlur={() => setEmailFocused(false)}
                                required
                            />
                            <FiMail className="input-icon" />
                        </div>

                        <div className={`input-container ${passwordFocused ? 'focus' : ''}`}>
                            <label htmlFor="password">Parola</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Introduceți parola"
                                onFocus={() => setPasswordFocused(true)}
                                onBlur={() => setPasswordFocused(false)}
                                required
                            />
                            <FiLock className="input-icon" />
                        </div>

                        <div className="login-options">
                            <div className="remember-me">
                                <input
                                    type="checkbox"
                                    id="remember"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <label htmlFor="remember">Ține-mă minte</label>
                            </div>
                            <a href="/forgot-password" className="forgot-password">Am uitat parola</a>
                        </div>

                        <button
                            type="submit"
                            className={`login-btn ${isLoading ? 'loading' : ''}`}
                            onClick={createRipple}
                            ref={buttonRef}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner"></span>
                                    <span className="btn-text">Se conectează...</span>
                                </>
                            ) : (
                                <span className="btn-text">Autentificare</span>
                            )}
                        </button>

                        {errorMessage && <div className="error-message">{errorMessage}</div>}
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;